// 팀 성과 관련 유틸리티 함수들

/**
 * 달성률에 따른 진행률 색상 결정
 */
export const getProgressColor = (achievementRate: number): string => {
  if (achievementRate >= 1.0) return 'bg-green-500';
  if (achievementRate >= 0.8) return 'bg-yellow-500';
  return 'bg-red-500';
};

/**
 * 달성률 상태 칩 — 진행 바(getProgressColor)와 동일 임계(100% 달성 / 80% 근접 / 미만 미달).
 * 칩·바·텍스트가 한 행에서 서로 다른 판정을 내지 않도록 단일 정본으로 사용한다.
 * @param achievementRate 분수(1.0 = 100%)
 */
export const getAchievementStatus = (achievementRate: number): { label: string; chipClass: string } => {
  if (achievementRate >= 1.0) return { label: '달성', chipClass: 'bg-green-100 text-green-800' };
  if (achievementRate >= 0.8) return { label: '근접', chipClass: 'bg-yellow-100 text-yellow-800' };
  return { label: '미달', chipClass: 'bg-red-100 text-red-800' };
};

/**
 * 사용자 역할과 팀명에 따른 데이터 마스킹 여부 확인
 */
export const shouldMaskTeamData = (
  teamName: string,
  userRole?: string,
  userTeamName?: string | null
): boolean => {
  // 직원 역할이 아니면 마스킹하지 않음
  if (userRole !== 'employee') return false;
  
  // 직원 역할이고 다른 팀이면 마스킹
  return userTeamName !== teamName;
};

/**
 * 실장 급여/인센티브 마스킹 여부 확인
 *
 * 원래 의도는 '팀장·팀원이 실장 급여를 못 보게'인데, 조건이 "admin이 아니면 전부 가림"이라
 * 실장 본인까지 걸려 자기 급여를 못 보는 상태였다(2026-08-04 확인).
 * → admin과 '본인'만 볼 수 있게 한다. 타인은 종전대로 가려진다.
 *
 * @param isSelf 로그인 계정이 이 직원 본인인가 (계정의 employeeId ↔ 직원 id 수동 연결로 판정).
 *               인자를 넘기지 않는 기존 호출부는 종전 동작(admin만)을 그대로 유지한다.
 */
export const shouldMaskDirectorData = (
  employeePosition: string,
  userRole?: string,
  isSelf: boolean = false
): boolean => {
  if (employeePosition !== '실장') return false;
  if (isSelf) return false;
  return userRole !== 'admin';
};

/**
 * 로그인 계정이 해당 직원 본인인지 판정한다.
 * 계정에 employeeId가 지정되지 않았으면 항상 false — 이름 자동 매칭은 하지 않는다
 * (동명이인 오매칭 시 남의 급여가 열리므로, 연결은 관리자가 화면에서 수동 지정한다).
 */
export const isSelfEmployee = (
  userEmployeeId?: string | null,
  employeeId?: string | number | null
): boolean => {
  if (userEmployeeId === undefined || userEmployeeId === null || userEmployeeId === '') return false;
  if (employeeId === undefined || employeeId === null || employeeId === '') return false;
  return String(userEmployeeId) === String(employeeId);
};

/**
 * 팀 데이터 정렬 함수
 */
export const sortTeamData = (data: any[], key: string, direction: 'asc' | 'desc') => {
  return [...data].sort((a, b) => {
    let aValue = a[key];
    let bValue = b[key];
    
    // 📈 초과/미달 컬럼 특별 처리 - 초과매출과 미달성매출을 합쳐서 계산
    if (key === 'exceededSales') {
      // 초과매출이 있으면 양수, 미달성매출이 있으면 음수로 계산
      aValue = a.exceededSales > 0 ? a.exceededSales : -a.underperformanceSales;
      bValue = b.exceededSales > 0 ? b.exceededSales : -b.underperformanceSales;
    }
    
    // 문자열인 경우
    if (typeof aValue === 'string') {
      return direction === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // 숫자인 경우
    return direction === 'asc' 
      ? aValue - bValue
      : bValue - aValue;
  });
};


/**
 * 팀 성과 요약 데이터 계산
 */
export const calculatePerformanceSummary = (
  performanceData: any[],
  userRole?: string,
  userTeamName?: string | null
) => {
  // 사용자 역할에 따른 데이터 필터링
  const filteredData = userRole === 'employee' && userTeamName
    ? performanceData.filter(team => team.teamName === userTeamName)
    : performanceData;

  // 모든 합계 한 번에 계산
  const summary = filteredData.reduce((acc, team) => ({
    totalTargetSales: acc.totalTargetSales + team.targetSales,
    totalActualSales: acc.totalActualSales + team.actualSales,
    totalExceededSales: acc.totalExceededSales + team.exceededSales,
    totalUnderperformanceSales: acc.totalUnderperformanceSales + team.underperformanceSales,
    totalDeductionAmount: acc.totalDeductionAmount + team.deductionAmount,
    totalIncentiveAmount: acc.totalIncentiveAmount + team.incentiveAmount,
  }), {
    totalTargetSales: 0,
    totalActualSales: 0,
    totalExceededSales: 0,
    totalUnderperformanceSales: 0,
    totalDeductionAmount: 0,
    totalIncentiveAmount: 0,
  });

  // 달성률 계산
  const achievementRate = summary.totalTargetSales > 0 
    ? (summary.totalActualSales / summary.totalTargetSales) * 100
    : 0;

  return {
    ...summary,
    achievementRate,
    filteredData
  };
}; 